import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/providers/AuthProvider';
import { 
  Loader2, 
  Palette, 
  Users, 
  Shield, 
  CheckCircle, 
  Wifi, 
  Database, 
  FileCode
} from 'lucide-react';

interface PreloaderProps {
  onComplete: () => void;
  minDisplayTime?: number;
  authLoading: boolean;
}

interface VerificationCheck {
  id: string;
  label: string;
  icon: React.ElementType;
  status: 'pending' | 'checking' | 'passed' | 'failed' | 'skipped';
  critical: boolean;
}

const Preloader: React.FC<PreloaderProps> = ({ onComplete, minDisplayTime = 500, authLoading }) => {
  const { user, isAdmin } = useAuth();
  const [isVisible, setIsVisible] = useState(true);
  const [progress, setProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [allChecksComplete, setAllChecksComplete] = useState(false);
  const startTimeRef = useRef(Date.now());
  const completionTriggeredRef = useRef(false);
  
  // Verification checks state
  const [checks, setChecks] = useState<VerificationCheck[]>([
    { id: 'dom', label: 'DOM Ready', icon: FileCode, status: 'pending', critical: true },
    { id: 'fonts', label: 'Fonts', icon: Palette, status: 'pending', critical: false },
    { id: 'network', label: 'Network', icon: Wifi, status: 'pending', critical: true },
    { id: 'storage', label: 'Storage', icon: Database, status: 'pending', critical: false },
    { id: 'auth', label: 'Auth', icon: Shield, status: 'pending', critical: true },
  ]);

  // Update a specific check's status
  const updateCheck = useCallback((id: string, status: VerificationCheck['status']) => {
    setChecks(prev => prev.map(check => 
      check.id === id ? { ...check, status } : check
    ));
  }, []);

  // Get user's display name with proper capitalization
  const getDisplayName = useCallback(() => {
    if (user?.user_metadata?.first_name && user?.user_metadata?.last_name) {
      const firstName = user.user_metadata.first_name.toLowerCase();
      const lastName = user.user_metadata.last_name.toLowerCase();
      return `${firstName.charAt(0).toUpperCase() + firstName.slice(1)} ${lastName.charAt(0).toUpperCase() + lastName.slice(1)}`;
    }
    if (user?.user_metadata?.first_name) {
      const firstName = user.user_metadata.first_name.toLowerCase();
      return firstName.charAt(0).toUpperCase() + firstName.slice(1);
    }
    if (user?.email) {
      const emailName = user.email.split('@')[0].toLowerCase();
      return emailName.charAt(0).toUpperCase() + emailName.slice(1);
    }
    return null;
  }, [user]);

  // Get greeting message based on user status
  const getGreeting = useCallback(() => {
    const name = getDisplayName();
    
    if (isAdmin && name) {
      return {
        primary: `Welcome back, Admin ${name}`,
        secondary: "Ready to manage your auction empire?",
        icon: Users,
        color: "text-blue-400"
      };
    } else if (user && name) {
      return {
        primary: `Welcome back, ${name}`,
        secondary: "Ready to discover amazing artworks?",
        icon: Users,
        color: "text-emerald-400"
      };
    } else {
      return {
        primary: "Florida Highwaymen Auction",
        secondary: "Discover extraordinary art and timeless treasures",
        icon: Palette,
        color: "text-teal-400"
      };
    }
  }, [user, isAdmin, getDisplayName]);

  const greeting = getGreeting();

  // Run all verification checks in parallel for maximum speed
  useEffect(() => {
    const QUICK_TIMEOUT = 400; // Fast timeout per check
    
    const runChecks = async () => {
      // Start all checks simultaneously
      const checkPromises = [
        // DOM Ready check
        (async () => {
          updateCheck('dom', 'checking');
          try {
            if (document.readyState === 'complete') {
              updateCheck('dom', 'passed');
              return true;
            }
            await new Promise<void>((resolve) => {
              if (document.readyState === 'complete') {
                resolve();
              } else {
                const handler = () => {
                  if (document.readyState === 'complete') {
                    window.removeEventListener('load', handler);
                    resolve();
                  }
                };
                window.addEventListener('load', handler);
                setTimeout(resolve, QUICK_TIMEOUT);
              }
            });
            updateCheck('dom', 'passed');
            return true;
          } catch {
            updateCheck('dom', 'passed'); // DOM is always accessible
            return true;
          }
        })(),

        // Fonts check
        (async () => {
          updateCheck('fonts', 'checking');
          try {
            if (document.fonts?.ready) {
              const result = await Promise.race([
                document.fonts.ready,
                new Promise(resolve => setTimeout(() => resolve('timeout'), QUICK_TIMEOUT))
              ]);
              updateCheck('fonts', result === 'timeout' ? 'skipped' : 'passed');
            } else {
              updateCheck('fonts', 'skipped');
            }
            return true;
          } catch {
            updateCheck('fonts', 'skipped');
            return true;
          }
        })(),

        // Network check
        (async () => {
          updateCheck('network', 'checking');
          try {
            const isOnline = navigator.onLine;
            // Quick connectivity verification
            if (isOnline) {
              updateCheck('network', 'passed');
            } else {
              updateCheck('network', 'failed');
            }
            return isOnline;
          } catch {
            updateCheck('network', 'skipped');
            return true;
          }
        })(),

        // Storage check
        (async () => {
          updateCheck('storage', 'checking');
          try {
            const testKey = '__preloader_test__';
            localStorage.setItem(testKey, '1');
            localStorage.removeItem(testKey);
            sessionStorage.setItem(testKey, '1');
            sessionStorage.removeItem(testKey);
            updateCheck('storage', 'passed');
            return true;
          } catch {
            updateCheck('storage', 'skipped');
            return true; // Not critical
          }
        })(),
      ];

      // Run all checks in parallel
      await Promise.allSettled(checkPromises);
      
      // Mark checks as complete
      setAllChecksComplete(true);
    };

    runChecks();
  }, [updateCheck]);

  // Handle auth status separately as it comes from props
  useEffect(() => {
    if (!authLoading) {
      updateCheck('auth', 'passed');
    } else {
      updateCheck('auth', 'checking');
    }
  }, [authLoading, updateCheck]);

  // Fast progress animation - completes in ~1.5 seconds
  useEffect(() => {
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        // Cap at 95% until all checks complete, then race to 100%
        const canComplete = allChecksComplete && !authLoading;
        const targetMax = canComplete ? 100 : 95;
        
        if (prev >= targetMax) {
          if (targetMax === 100 && prev >= 100) {
            setIsComplete(true);
            clearInterval(progressInterval);
          }
          return Math.min(prev, 100);
        }
        
        // Fast exponential progress
        const remaining = targetMax - prev;
        const speed = canComplete ? 0.25 : 0.15;
        const increment = Math.max(3, remaining * speed);
        
        return Math.min(prev + increment, targetMax);
      });
    }, 40); // Fast 40ms ticks

    return () => clearInterval(progressInterval);
  }, [allChecksComplete, authLoading]);

  // Maximum timeout safeguard - 2 seconds max (was 4 seconds)
  useEffect(() => {
    const MAX_PRELOADER_TIME = 2000;
    const maxTimer = setTimeout(() => {
      console.log('Max preloader timeout reached, forcing completion');
      setAllChecksComplete(true);
      setProgress(100);
      setIsComplete(true);
    }, MAX_PRELOADER_TIME);

    return () => clearTimeout(maxTimer);
  }, []);

  // Handle completion - fast exit
  useEffect(() => {
    if (isComplete && !completionTriggeredRef.current) {
      completionTriggeredRef.current = true;
      const elapsed = Date.now() - startTimeRef.current;
      const remainingMinTime = Math.max(0, minDisplayTime - elapsed);
      
      const timer = setTimeout(() => {
        setIsVisible(false);
        // Immediate callback for snappy feel
        requestAnimationFrame(() => {
          onComplete();
        });
      }, Math.min(remainingMinTime, 150)); // Max 150ms wait

      return () => clearTimeout(timer);
    }
  }, [isComplete, onComplete, minDisplayTime]);

  if (!isVisible) {
    return null;
  }

  // Count passed checks for display
  const passedChecks = checks.filter(c => c.status === 'passed' || c.status === 'skipped').length;
  const totalChecks = checks.length;

  return (
    <div className="fixed inset-0 z-50 bg-gradient-to-br from-emerald-950 via-teal-900 to-blue-950 overflow-hidden">
      {/* Subtle animated background - lighter for performance */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/3 w-[400px] h-[400px] bg-emerald-400/8 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/3 w-[400px] h-[400px] bg-blue-400/8 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '0.5s' }} />
      </div>

      {/* Main centered card */}
      <div className="flex items-center justify-center min-h-screen px-4 py-8 relative">
        <div className="relative max-w-sm w-full">
          {/* Glass morphism card - more compact */}
          <div className="relative bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 shadow-2xl overflow-hidden p-6 md:p-8">
            {/* Card glow effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 via-transparent to-blue-500/5 pointer-events-none" />
            
            {/* Content */}
            <div className="relative z-10">
              {/* Compact circular progress with logo */}
              <div className="flex justify-center mb-6">
                <div className="relative w-28 h-28">
                  {/* Circular progress track */}
                  <svg className="w-28 h-28 transform -rotate-90">
                    <circle
                      cx="56"
                      cy="56"
                      r="48"
                      stroke="rgba(255,255,255,0.1)"
                      strokeWidth="3"
                      fill="none"
                    />
                    <circle
                      cx="56"
                      cy="56"
                      r="48"
                      stroke="url(#progressGradient)"
                      strokeWidth="3"
                      fill="none"
                      strokeLinecap="round"
                      strokeDasharray={`${2 * Math.PI * 48}`}
                      strokeDashoffset={`${2 * Math.PI * 48 * (1 - progress / 100)}`}
                      className="transition-all duration-150 ease-out"
                    />
                    <defs>
                      <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor={isComplete ? "#34d399" : "#60a5fa"} />
                        <stop offset="100%" stopColor={isComplete ? "#14b8a6" : "#06b6d4"} />
                      </linearGradient>
                    </defs>
                  </svg>
                  
                  {/* Center logo */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className={`w-16 h-16 rounded-full bg-gradient-to-br from-emerald-500 via-teal-500 to-blue-600 flex items-center justify-center shadow-xl transition-transform duration-300 ${isComplete ? 'scale-110' : 'scale-100'}`}>
                      {isComplete ? (
                        <CheckCircle className="h-8 w-8 text-white animate-in zoom-in duration-200" />
                      ) : (
                        <span className="text-white font-light text-2xl">F</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Title and branding - compact */}
              <div className="text-center mb-4">
                <h1 className="text-xl md:text-2xl font-light mb-1 bg-gradient-to-r from-emerald-200 via-teal-200 to-blue-200 bg-clip-text text-transparent">
                  Florida Highwaymen
                </h1>
                <p className="text-sm text-white/50 font-light">Auction House</p>
              </div>

              {/* Greeting section - compact */}
              <div className="text-center mb-5">
                <div className="inline-flex items-center space-x-2 px-3 py-1.5 bg-white/5 rounded-full border border-white/10">
                  <greeting.icon className={`h-3.5 w-3.5 ${greeting.color}`} />
                  <p className="text-xs text-white/80 font-light">{greeting.primary}</p>
                </div>
              </div>

              {/* Verification checks grid - compact inline display */}
              <div className="flex justify-center items-center gap-2 mb-4 flex-wrap">
                {checks.map((check) => {
                  const IconComponent = check.icon;
                  const statusColor = {
                    pending: 'text-white/30',
                    checking: 'text-blue-400 animate-pulse',
                    passed: 'text-emerald-400',
                    failed: 'text-red-400',
                    skipped: 'text-yellow-400/60'
                  }[check.status];
                  
                  return (
                    <div
                      key={check.id}
                      className="flex items-center gap-1 px-2 py-1 bg-white/5 rounded-full border border-white/5"
                      title={`${check.label}: ${check.status}`}
                    >
                      <IconComponent className={`h-3 w-3 ${statusColor} transition-colors duration-200`} />
                      {check.status === 'passed' && (
                        <CheckCircle className="h-2.5 w-2.5 text-emerald-400" />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Progress info */}
              <div className="text-center space-y-2">
                <div className="flex items-center justify-center gap-2">
                  <span className={`text-lg font-medium tabular-nums transition-colors duration-200 ${
                    isComplete ? 'text-emerald-300' : 'text-white/90'
                  }`}>
                    {Math.round(progress)}%
                  </span>
                  {!isComplete && (
                    <Loader2 className="h-4 w-4 text-blue-400 animate-spin" />
                  )}
                </div>
                <p className="text-xs text-white/50">
                  {isComplete 
                    ? `✓ All ${totalChecks} checks passed` 
                    : `Verifying ${passedChecks}/${totalChecks} checks...`
                  }
                </p>
              </div>

              {/* Linear progress bar for visual feedback */}
              <div className="mt-4 h-1 bg-white/10 rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all duration-150 ease-out ${
                    isComplete 
                      ? 'bg-gradient-to-r from-emerald-400 to-teal-400' 
                      : 'bg-gradient-to-r from-blue-400 to-cyan-400'
                  }`}
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </div>

          {/* Footer text */}
          <div className="text-center mt-4">
            <p className="text-xs text-white/30 font-light">
              {isComplete ? 'Ready' : 'Loading...'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Preloader;
